/**
 * Página de Cadastro de Procedimentos
 * Tela auxiliar para pré-cadastro de procedimentos (NÃO aparece no menu)
 */

import React, { useState, useEffect } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useTheme as useAppTheme } from '../../context/ThemeContext';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Grid,
  Alert,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import procedimentoService from '../../services/procedimentoService';

const Procedimentos = () => {
  const { user } = useAuth();
  const { isDarkMode } = useAppTheme();

  // Função para formatar valor como moeda BRL
  function formatarMoeda(valor) {
    if (!valor) return '';
    // Remove tudo que não for número
    valor = valor.toString().replace(/\D/g, '');
    // Converte para centavos
    valor = (parseInt(valor, 10) / 100).toFixed(2);
    // Formata para BRL
    return valor.replace('.', ',');
  }
  const planoAtual = user?.plano || user?.clinica?.plano || 'FREE';
  const [procedimentos, setProcedimentos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [modalOpen, setModalOpen] = useState(false);
  const [editando, setEditando] = useState(null);
  const [pesquisa, setPesquisa] = useState('');
  
  const [formData, setFormData] = useState({
    codigo: '',
    nome: '',
    descricao: '',
    valorPadrao: '',
    categoria: '',
    observacoes: '',
    ativo: true
  });

  useEffect(() => {
    carregarProcedimentos();
  }, []);

  const carregarProcedimentos = async () => {
    try {
      setLoading(true);
      console.log('🔵 Carregando procedimentos...');
      const data = await procedimentoService.listar();
      console.log('✅ Dados recebidos:', data);
      console.log('✅ É array?', Array.isArray(data));
      console.log('✅ Quantidade:', data?.length);
      setProcedimentos(Array.isArray(data) ? data : []);
      setError('');
    } catch (err) {
      setError('Erro ao carregar procedimentos');
      console.error('❌ Erro ao carregar:', err);
      setProcedimentos([]); // Garante que sempre seja um array
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (procedimento = null) => {
    if (procedimento) {
      setEditando(procedimento);
      setFormData({
        ...procedimento,
        valorPadrao: procedimento.valorPadrao || ''
      });
    } else {
      setEditando(null);
      setFormData({
        codigo: '',
        nome: '',
        descricao: '',
        valorPadrao: '',
        categoria: '',
        observacoes: '',
        ativo: true
      });
    }
    setModalOpen(true);
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setEditando(null);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      const dados = {
        ...formData,
        valorPadrao: formData.valorPadrao ? parseFloat(formData.valorPadrao) : null
      };

      if (editando) {
        await procedimentoService.atualizar(editando.id, dados);
        setSuccess('Procedimento atualizado com sucesso!');
      } else {
        await procedimentoService.criar(dados);
        setSuccess('Procedimento cadastrado com sucesso!');
      }
      
      handleCloseModal();
      carregarProcedimentos();
      
      setTimeout(() => setSuccess(''), 3000);
    } catch (err) {
      setError('Erro ao salvar procedimento');
      console.error(err);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Deseja realmente excluir este procedimento?')) {
      try {
        await procedimentoService.deletar(id);
        setSuccess('Procedimento excluído com sucesso!');
        carregarProcedimentos();
        setTimeout(() => setSuccess(''), 3000);
      } catch (err) {
        setError('Erro ao excluir procedimento');
        console.error(err);
      }
    }
  };

  const procedimentosFiltrados = (procedimentos || []).filter(p =>
    p.nome?.toLowerCase().includes(pesquisa.toLowerCase()) ||
    (p.codigo && p.codigo.toLowerCase().includes(pesquisa.toLowerCase())) ||
    (p.categoria && p.categoria.toLowerCase().includes(pesquisa.toLowerCase()))
  );

  const limiteProcedimentosFree = 10;
  const atingiuLimiteFree = planoAtual === 'FREE' && procedimentos.length >= limiteProcedimentosFree;

  return (
    <Box sx={{ p: 3, backgroundColor: isDarkMode ? undefined : '#FFFAF5', minHeight: '100vh' }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ borderLeft: '4px solid #F97316', pl: 1.5 }}>
            Cadastro de Procedimentos
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, pl: 1.5 }}>
            Cadastros auxiliares para facilitar os lançamentos
          </Typography>
        </Box>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => handleOpenModal()}
          disabled={atingiuLimiteFree}
          sx={{
            bgcolor: '#F97316',
            '&:hover': { bgcolor: '#EA580C' },
            boxShadow: '0 2px 10px rgba(249,115,22,0.35)',
            '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
          }}
        >
          Novo Procedimento
        </Button>
      </Box>
      {atingiuLimiteFree && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          Limite de 10 procedimentos atingido no plano grátis. Para cadastrar mais procedimentos, faça upgrade para o plano Pro.
        </Alert>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}
      {success && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess('')}>{success}</Alert>}

      <TextField
        fullWidth
        placeholder="Pesquisar por nome, código ou categoria..."
        value={pesquisa}
        onChange={(e) => setPesquisa(e.target.value)}
        InputProps={{
          startAdornment: <SearchIcon sx={{ mr: 1, color: '#F97316' }} />
        }}
        sx={{
          mb: 3,
          '& .MuiOutlinedInput-root': {
            '&:hover fieldset': { borderColor: 'rgba(249,115,22,0.5)' },
            '&.Mui-focused fieldset': { borderColor: '#F97316' },
          }
        }}
      />

      <TableContainer component={Paper} sx={{
        borderTop: '3px solid #F97316',
        boxShadow: '0 2px 12px rgba(249,115,22,0.08)',
        background: isDarkMode ? undefined : '#fff',
      }}>
        <Table>
          <TableHead>
            <TableRow sx={{ background: isDarkMode ? 'rgba(249,115,22,0.08)' : 'rgba(249,115,22,0.06)' }}>
              <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Código</TableCell>
              <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Nome</TableCell>
              <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Categoria</TableCell>
              <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Valor Padrão</TableCell>
              <TableCell sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Status</TableCell>
              <TableCell align="right" sx={{ fontWeight: 700, textTransform: 'uppercase', fontSize: '0.72rem', color: '#F97316', letterSpacing: '0.5px' }}>Ações</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Carregando...</TableCell>
              </TableRow>
            ) : procedimentosFiltrados.length === 0 ? (
              <TableRow>
                <TableCell colSpan={6} align="center">Nenhum procedimento encontrado</TableCell>
              </TableRow>
            ) : (
              procedimentosFiltrados.map((procedimento) => (
                <TableRow
                  key={procedimento.id}
                  sx={{
                    borderBottom: '1px solid rgba(249,115,22,0.1)',
                    '&:hover': { backgroundColor: 'rgba(249,115,22,0.04) !important' }
                  }}
                >
                  <TableCell sx={{ color: 'text.secondary', fontSize: '0.85rem' }}>{procedimento.codigo || '-'}</TableCell>
                  <TableCell sx={{ fontWeight: 500 }}>{procedimento.nome}</TableCell>
                  <TableCell>
                    {procedimento.categoria ? (
                      <Chip
                        label={procedimento.categoria}
                        size="small"
                        sx={{
                          bgcolor: 'rgba(249,115,22,0.1)',
                          color: '#EA580C',
                          fontWeight: 600,
                          fontSize: '0.72rem',
                          border: '1px solid rgba(249,115,22,0.2)'
                        }}
                      />
                    ) : '-'}
                  </TableCell>
                  <TableCell sx={{ fontWeight: 600 }}>
                    {procedimento.valorPadrao
                      ? `R$ ${parseFloat(procedimento.valorPadrao).toFixed(2)}`
                      : '-'}
                  </TableCell>
                  <TableCell>
                    <Chip
                      label={procedimento.ativo ? 'Ativo' : 'Inativo'}
                      size="small"
                      sx={procedimento.ativo ? {
                        bgcolor: 'rgba(34,197,94,0.12)',
                        color: '#16A34A',
                        fontWeight: 600,
                        fontSize: '0.72rem'
                      } : {
                        fontWeight: 600,
                        fontSize: '0.72rem'
                      }}
                    />
                  </TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton size="small" onClick={() => handleOpenModal(procedimento)} sx={{ color: '#F97316' }}>
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Excluir">
                      <IconButton size="small" onClick={() => handleDelete(procedimento.id)} color="error">
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* Modal de Cadastro/Edição */}
      <Dialog
        open={modalOpen}
        onClose={handleCloseModal}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: isDarkMode
            ? { background: 'rgba(30,41,59,0.92)', backdropFilter: 'blur(12px)', border: '1px solid rgba(249,115,22,0.2)' }
            : { background: '#FFFAF5', border: '1px solid rgba(249,115,22,0.2)' }
        }}
      >
        <DialogTitle sx={{ borderLeft: '4px solid #F97316', pl: 2, fontWeight: 700 }}>
          {editando ? 'Editar Procedimento' : 'Novo Procedimento'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Código"
                name="codigo"
                value={formData.codigo}
                onChange={handleChange}
                placeholder="Ex: TUSS 81000030"
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Nome *"
                name="nome"
                value={formData.nome}
                onChange={handleChange}
                placeholder="Ex: Restauração de dente"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Categoria"
                name="categoria"
                value={formData.categoria}
                onChange={handleChange}
                placeholder="Ex: Prevenção, Restauração"
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Valor Padrão"
                name="valorPadrao"
                value={formData.valorPadrao}
                onChange={e => {
                  const raw = e.target.value;
                  // Permite digitar só números
                  const formatado = formatarMoeda(raw);
                  // Atualiza o formData
                  handleChange({ target: { name: 'valorPadrao', value: formatado } });
                }}
                InputProps={{
                  startAdornment: <InputAdornment position="start">R$</InputAdornment>,
                  inputMode: 'numeric',
                  pattern: '[0-9]*'
                }}
                placeholder="0,00"
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={3}
                label="Descrição"
                name="descricao"
                value={formData.descricao}
                onChange={handleChange}
                placeholder="Descreva os detalhes do procedimento..."
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Observações"
                name="observacoes"
                value={formData.observacoes}
                onChange={handleChange}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={handleCloseModal} sx={{ color: 'text.secondary' }}>Cancelar</Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={!formData.nome}
            sx={{
              bgcolor: '#F97316',
              '&:hover': { bgcolor: '#EA580C' },
              boxShadow: '0 2px 10px rgba(249,115,22,0.35)',
              '&.Mui-disabled': { bgcolor: 'action.disabledBackground' }
            }}
          >
            {editando ? 'Atualizar' : 'Cadastrar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Procedimentos;
